import type { Audience } from "@/lib/audience";

// Pure lane-assignment logic for the CareerGraph "Modern" view, split out of
// the component so it stays unit-testable and the component file only exports
// components (React Fast Refresh). See ADR-0023 for the branching rule.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EntryShape = any;

export interface RoleEntry {
  id: string;
  kind: "experience" | "education";
  sourceId: string;
  title: string;
  company: string;
  location: string;
  country: string;
  industry: string;
  start: string;
  end: string;
  activities: string[];
  shared: ReadonlyArray<Audience> | null | undefined;
  entry: EntryShape;
  active?: boolean;
  lane?: number;
  countryName?: string;
  industryName?: string;
}

function monthsOf(v: string | null | undefined): number | null {
  if (!v) return null;
  const m = String(v).match(/(\d{4})-(\d{2})/);
  if (m) return Number(m[1]) * 12 + (Number(m[2]) - 1);
  const y = String(v).match(/\d{4}/);
  return y ? Number(y[0]) * 12 : null;
}

// [start, end] as month numbers; an ongoing role (no end) extends to +Infinity.
function monthRange(r: { start: string; end: string }): [number, number] {
  const s = monthsOf(r.start) ?? -Infinity;
  const e = r.end ? monthsOf(r.end) ?? s : Infinity;
  return [s, e];
}

// Strict overlap: two roles share calendar time. Touching at a single boundary
// month (one ends Oct, the next starts Oct) is sequential, not parallel, so we
// compare strictly.
function rangesOverlap(a: [number, number], b: [number, number]): boolean {
  return a[0] < b[1] && b[0] < a[1];
}

// Lane-Zuweisung nach ADR-0023, losgeloest von EntryShape: lane 0 ist der
// Hauptpfad, jeder Eintrag nimmt die niedrigste Lane, mit deren Eintraegen er
// sich zeitlich nicht ueberschneidet. Erwartet die Eintraege in Anzeige-
// reihenfolge (neueste zuerst) und setzt `lane` direkt auf ihnen.
//
// `start`/`end` sind Text. Eine reine Jahresangabe ("2003") wird als Januar
// dieses Jahres gelesen, deshalb funktioniert die Zuweisung auch fuer Quellen
// ohne Monatsgenauigkeit — Filmografien etwa kennen nur Erscheinungsjahre.
export function assignLanes<T extends { start: string; end: string; lane?: number }>(
  items: readonly T[],
): number {
  const laneRanges: Array<Array<[number, number]>> = [];
  for (const item of items) {
    const range = monthRange(item);
    let placed = laneRanges.findIndex((occupied) =>
      !occupied.some((o) => rangesOverlap(o, range)),
    );
    if (placed === -1) {
      placed = laneRanges.length;
      laneRanges.push([]);
    }
    laneRanges[placed]!.push(range);
    item.lane = placed;
  }
  return Math.max(1, laneRanges.length);
}

export function buildRoles(
  experiences: readonly EntryShape[] | null | undefined,
  educations: readonly EntryShape[] | null | undefined,
): { roles: RoleEntry[]; laneCount: number } {
  const roles: RoleEntry[] = [];
  for (const e of experiences ?? []) {
    roles.push({
      id: `exp-${e.id}`,
      kind: "experience",
      sourceId: e.id,
      title: e.jobTitle || "–",
      company: [e.employer, e.department].filter(Boolean).join(" · "),
      location: e.location || "",
      country: e.country?.id ?? e.country ?? "",
      industry: e.industry?.id ?? e.industry ?? "",
      start: e.startDate,
      end: e.endDate,
      activities: String(e.activities ?? "")
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0),
      shared: e.sharedWith,
      entry: e,
    });
  }
  for (const ed of educations ?? []) {
    roles.push({
      id: `edu-${ed.id}`,
      kind: "education",
      sourceId: ed.id,
      title: [ed.degree?.name, ed.field].filter(Boolean).join(" · ") || ed.institution || "–",
      company: ed.institution || "",
      location: ed.city || "",
      country: ed.country?.id ?? ed.country ?? "",
      industry: "",
      start: ed.startDate,
      end: ed.endDate,
      activities: [],
      shared: ed.sharedWith,
      entry: ed,
    });
  }
  roles.sort((a, b) => String(b.start || "").localeCompare(String(a.start || "")));
  roles.forEach((r) => {
    r.active = !r.end;
  });

  // Lane assignment (ADR-0023): lane 0 is the main career path. Roles are
  // processed newest-first (display order); each takes the lowest lane whose
  // roles it does not time-overlap. So sequential roles all stay on the trunk
  // and only genuine concurrency (two jobs, or studying while working) pushes
  // a role onto a branch lane. Lanes are reused once free, and since intervals
  // are time-ordered the lanes never cross. This deliberately replaces
  // workshop's branch-per-role design (ADR-0001).
  const laneCount = assignLanes(roles);
  return { roles, laneCount };
}
